/**
 * Notifications.
 *
 * Storage model: a global `notification_events` table plus a per-user
 * `notification_reads` join. This avoids fanning out the same event to N rows
 * (one per user) and keeps the feed scalable.
 *
 * The feed served to the current user is computed by joining events with
 * reads (left join) and surfacing unread state.
 */

import { Type, type Static } from '@sinclair/typebox';
import { NotificationTypes } from '../enums.js';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

// ---------------------------------------------------------------------------
// Notification event (global)
// ---------------------------------------------------------------------------

export const NotificationEventSchema = Type.Object({
  id: UuidSchema,
  notificationType: Type.Union(NotificationTypes.map((n) => Type.Literal(n))),

  /** Optional references — surfaced as deep-links in the UI. */
  projectId: Type.Union([UuidSchema, Type.Null()]),
  projectItemId: Type.Union([UuidSchema, Type.Null()]),
  orderBatchId: Type.Union([UuidSchema, Type.Null()]),

  title: Type.String({ minLength: 1, maxLength: 200 }),
  body: Type.String({ maxLength: 1000 }),
  metadataJson: Type.Union([Type.String(), Type.Null()]),

  createdAt: IsoDateTimeSchema,
});
export type NotificationEvent = Static<typeof NotificationEventSchema>;

// ---------------------------------------------------------------------------
// Feed entry — per-user view (joins event + read state)
// ---------------------------------------------------------------------------

export const NotificationFeedEntrySchema = Type.Composite([
  NotificationEventSchema,
  Type.Object({
    /** Read timestamp for the current user; null if unread. */
    readAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
  }),
]);
export type NotificationFeedEntry = Static<typeof NotificationFeedEntrySchema>;

// ---------------------------------------------------------------------------
// Feed response
// ---------------------------------------------------------------------------

export const NotificationFeedResponseSchema = Type.Object({
  items: Type.Array(NotificationFeedEntrySchema),
  unreadCount: Type.Integer({ minimum: 0 }),
});
export type NotificationFeedResponse = Static<typeof NotificationFeedResponseSchema>;

// ---------------------------------------------------------------------------
// Mark-read request
// ---------------------------------------------------------------------------

export const NotificationMarkReadSchema = Type.Object({
  notificationEventIds: Type.Array(UuidSchema, { minItems: 1, maxItems: 100 }),
});
export type NotificationMarkRead = Static<typeof NotificationMarkReadSchema>;
